import React, { memo, useEffect, useRef } from "react";

const EMBED_SRC =
  "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";

/**
 * One configured chart pane.
 *
 * The embed reads its settings from the JSON inside the injected <script> and
 * renders an iframe into the surrounding container. There is no API to update
 * a live widget, so every config change is a teardown and re-create — that
 * teardown is what makes symbol switching, intervals and multi-pane layouts
 * possible at all.
 *
 * The original version injected the script once behind a `hasRendered` guard
 * and never cleaned up, which is why the symbol was effectively frozen.
 */
const TradingViewChart = ({ symbol, interval, theme }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;

    /**
     * Creation is deferred by a tick on purpose.
     *
     * The embed script is async and reads its own parent element once it
     * loads. StrictMode mounts, cleans up, and remounts effects back-to-back
     * in development — so appending synchronously meant the cleanup tore the
     * container down before the script finished, and it then dereferenced a
     * null parent ("Cannot read properties of null"). Waiting a tick lets the
     * throwaway pass cancel before anything is injected.
     */
    const timer = window.setTimeout(() => {
      if (cancelled) return;

      container.innerHTML = "";

      const host = document.createElement("div");
      host.className = "chart__widget";
      container.appendChild(host);

      // Read rather than hard-code, so the light/dark override on `--bg` keeps
      // the embed canvas and the badge mask below the exact same colour.
      const background = getComputedStyle(container)
        .getPropertyValue("--bg")
        .trim();

      const script = document.createElement("script");
      script.src = EMBED_SRC;
      script.type = "text/javascript";
      script.async = true;
      script.innerHTML = JSON.stringify({
        autosize: true,
        symbol,
        interval,
        timezone: "Etc/UTC",
        theme,
        backgroundColor: background,
        style: "1",
        locale: "en",
        enable_publishing: false,
        allow_symbol_change: true,
        // The app supplies its own symbol/interval controls in the toolbar, so
        // the embed's duplicate row is redundant.
        hide_top_toolbar: true,
        // No watchlist/details/hotlist/calendar — each one of these adds the
        // embed's right-hand panel and its widgets button.
        details: false,
        hotlist: false,
        calendar: false,
        support_host: "https://www.tradingview.com",
      });

      // Into `host`, not `container`. The embed injects its widget as a
      // sibling of its own <script>, so appending to the container landed the
      // chart *after* the full-height host div instead of inside it — putting
      // it exactly one container-height below the visible area.
      host.appendChild(script);
    }, 0);

    // Drop the iframe and script on unmount or reconfigure — otherwise every
    // layout change leaks an iframe per pane.
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      container.innerHTML = "";
    };
  }, [symbol, interval, theme]);

  return (
    <div className="chart">
      <div className="chart__container" ref={containerRef} />
      {/* The embed's TradingView badge sits inside a cross-origin iframe, so
          it can't be styled away — masking it is the only reach we have from
          out here. See the note in the README about attribution. */}
      <div className="chart__badge-mask" aria-hidden="true" />
    </div>
  );
};

export default memo(TradingViewChart);
